import { useMemo } from "react";
import intl from "react-intl-universal";
import { TbAlertTriangle, TbClock, TbShieldOff, TbUsers } from "react-icons/tb";

const TOTAL_FIELDS = [
  { key: "total", icon: TbUsers, labelKey: "baas.entidades.totals.total" },
  { key: "active", icon: TbUsers, labelKey: "baas.entidades.totals.active" },
  { key: "pending", icon: TbClock, labelKey: "baas.entidades.totals.pending" },
  { key: "suspended", icon: TbShieldOff, labelKey: "baas.entidades.totals.suspended" },
];

const TARGET_KEY_BY_TOTAL = {
  total: "totalTarget",
  active: "activeTarget",
  pending: "pendingTarget",
  suspended: "suspendedTarget",
};

export const BaasEntidadesTotalsSection = ({
  totals,
  targets,
  editing,
  onToggleEdit,
  onTargetChange,
  onSave,
  onCancel,
}) => {
  const overTargetKeys = useMemo(() => {
    const exceeded = new Set();
    Object.entries(TARGET_KEY_BY_TOTAL).forEach(([totalKey, targetKey]) => {
      if (totals[totalKey] > (targets[targetKey] ?? 0)) {
        exceeded.add(totalKey);
      }
    });
    return exceeded;
  }, [totals, targets]);

  return (
    <section className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="dashboard-section-label">{intl.get("baas.entidades.totalsSection")}</p>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button type="button" className="extract-filter-panel__btn" onClick={onCancel}>
                {intl.get("common.cancel")}
              </button>
              <button
                type="button"
                className="extract-filter-panel__btn extract-filter-panel__btn--primary"
                onClick={onSave}
              >
                {intl.get("baas.entidades.save")}
              </button>
            </>
          ) : (
            <button type="button" className="extract-filter-panel__btn" onClick={onToggleEdit}>
              {intl.get("baas.entidades.editTotals")}
            </button>
          )}
        </div>
      </div>

      <div className="dashboard-card-grid">
        {TOTAL_FIELDS.map(({ key, icon: Icon, labelKey }) => {
          const targetKey = TARGET_KEY_BY_TOTAL[key];
          const targetValue = targets[targetKey] ?? 0;
          const currentValue = totals[key] ?? 0;
          const exceeded = overTargetKeys.has(key);

          return (
            <div key={key} className="summary-card min-w-0 w-full">
              <div className="flex min-w-0 items-center gap-2">
                <div className="summary-card-icon">
                  <Icon className="h-4 w-4 text-[#e7a73f]" />
                </div>
                <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
                  {intl.get(labelKey)}
                </p>
                {exceeded && !editing ? (
                  <TbAlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden />
                ) : null}
              </div>

              {editing ? (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <label className="block text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.entidades.totals.current")}
                    <input
                      type="text"
                      readOnly
                      value={currentValue}
                      className="mt-1 h-9 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-brand-dark dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
                    />
                  </label>
                  <label className="block text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.entidades.totals.target")}
                    <input
                      type="number"
                      min={0}
                      value={targetValue}
                      onChange={(event) =>
                        onTargetChange(targetKey, Math.max(0, Number(event.target.value) || 0))
                      }
                      className="mt-1 h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
                    />
                  </label>
                </div>
              ) : (
                <>
                  <p className="mt-2 break-words text-xl font-bold leading-tight text-brand-dark dark:text-gray-100">
                    {currentValue}
                  </p>
                  <p className="mt-2 pt-1 text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.entidades.totals.targetLabel", { count: targetValue })}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
